import { useMemo } from 'react'
import { Container } from 'src/components/department/Container'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { useMobile } from 'src/hooks/useMobile'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getResponsiveVisibility } from 'src/utils/getResponsiveVisibility'

import { PictosDesktop } from './PictosDesktop'
import { PictosMobile } from './PictosMobile'

export type PictoItem = {
  image: string
  textColor?: 'preto' | 'branco'
  text: string
  subtext?: string
  altImage: string
  linkUrl: string
}

type PictosProps = {
  items: PictoItem[]
  parentComponent?: string
  section?: string
  locationOnPage?: string
  titleSection?: string
  enableSession?: boolean
  showOnDesktop?: boolean
  showOnMobile?: boolean
}

export type PictoCardInteractionHandlers = {
  onCardClick: (item: PictoItem, index: number) => void
  onCardInView: (item: PictoItem, index: number) => void
}

const DATA_BANNER_TYPE = 'Categories Shelf'

export function PictosV2(props: PictosProps) {
  const { isMobile } = useMobile()
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)

  const {
    section,
    locationOnPage,
    items,
    showOnDesktop,
    showOnMobile,
    enableSession,
    titleSection,
  } = props

  const visibilityClasses = useMemo(() => {
    return getResponsiveVisibility(showOnDesktop, showOnMobile)
  }, [showOnDesktop, showOnMobile])

  const onCardClick = (item: PictoItem, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: item.text
        ? `${DATA_BANNER_TYPE} | ${item.text}`
        : item.altImage
        ? `${DATA_BANNER_TYPE} | ${item.altImage}`
        : 'N/A',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })

    if (!window) {
      return
    }

    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'event_track',
      event_category: 'clique',
      event_action: item.text,
      event_label: 'Pagina de departamento',
    })
  }

  const onCardInView = (item: PictoItem, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...standardObject,
      ...getFeatureObject({
        section,
        locationOnPage,
        name: item.text
          ? `${DATA_BANNER_TYPE} | ${item.text}`
          : item.altImage
          ? `${DATA_BANNER_TYPE} | ${item.altImage}`
          : 'N/A',
        index,
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  if (!enableSession) {
    return null
  }

  return (
    <Container
      className={`!max-w-restructure-content-wide restructure-maxMobile:my-10 restructure-small-desktop:my-[40px] !px-0 ${visibilityClasses}`}
    >
      {isMobile ? (
        <PictosMobile
          items={items}
          titleSection={titleSection}
          onCardClick={onCardClick}
          onCardInView={onCardInView}
        />
      ) : (
        <PictosDesktop
          items={items}
          titleSection={titleSection}
          onCardClick={onCardClick}
          onCardInView={onCardInView}
        />
      )}
    </Container>
  )
}
